import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { companiesApi, financialYearsApi, healthApi } from '../lib/api';
import { useSessionStore } from '../stores/session';
import { useCompanyStore } from '../stores/company';
import { useUiStore } from '../stores/ui';
import { useShortcuts } from '../lib/keys';
import { Can, usePermission } from '../lib/permissions';
import { CompanySwitcher } from '../features/company/CompanySwitcher';
import { CheatSheetOverlay } from './CheatSheetOverlay';
import { CommandPalette } from './CommandPalette';
import type { FinancialYear } from '../lib/types';
import { OnboardingProgress } from '../features/onboarding/OnboardingPage';

const NAV_ITEMS: { to: string; label: string; permission?: string }[] = [
  { to: '/money', label: 'Money desk (F5/F6)' },
  { to: '/money/payment/new', label: '  New Payment (F5)', permission: 'voucher:payment:create' },
  { to: '/money/receipt/new', label: '  New Receipt (F6)', permission: 'voucher:receipt:create' },
  { to: '/money/debit-note/new', label: '  Debit Note', permission: 'voucher:debit_note:create' },
  { to: '/goods/debit-note/new', label: '  Physical purchase return', permission: 'voucher:debit_note:create' },
  { to: '/money/credit-note/new', label: '  Credit Note', permission: 'voucher:credit_note:create' },
  { to: '/goods/credit-note/new', label: '  Physical sales return', permission: 'voucher:credit_note:create' },
  { to: '/reports/outstandings', label: 'Outstandings & ageing', permission: 'report:outstandings:view' },
  { to: '/reports/cash-book', label: 'Cash Book', permission: 'report:cash_book:view' },
  { to: '/reports/bank-book', label: 'Bank Book', permission: 'report:bank_book:view' },
  { to: '/reports/pdc', label: 'PDC register', permission: 'report:pdc_register:view' },
  { to: '/reports/bank-reconciliation', label: 'Bank reconciliation', permission: 'bank_reconciliation:statement:view' },
  {
    to: '/coa',
    label: 'Chart of accounts',
    permission: 'master:account_group:view',
  },
  {
    to: '/masters/items',
    label: 'Items & GST rates',
    permission: 'master:item:view',
  },
  {
    to: '/masters/catalog',
    label: 'Item groups & UOM',
    permission: 'master:item_group:view',
  },
  {
    to: '/masters/party-addresses',
    label: 'Party addresses',
    permission: 'master:party_address:view',
  },
  {
    to: '/settings/voucher-classes',
    label: 'Voucher classes',
    permission: 'master:voucher_class:view',
  },
  {
    to: '/settings/invoice-series',
    label: 'Invoice series',
    permission: 'master:invoice_series:view',
  },
  {
    to: '/settings/posting-accounts',
    label: 'Posting accounts',
    permission: 'master:goods_posting_account_mapping:view',
  },
  {
    to: '/settings/print-templates',
    label: 'Print templates',
    permission: 'master:print_template:view',
  },
  { to: '/vouchers', label: 'Journal / Contra' },
  {
    to: '/goods/sales/new',
    label: 'Sales invoice (F8)',
    permission: 'voucher:sales:create',
  },
  {
    to: '/goods/purchase/new',
    label: 'Purchase voucher (F9)',
    permission: 'voucher:purchase:create',
  },
  {
    to: '/goods/sales/register',
    label: 'Sales register',
    permission: 'report:sales_register:view',
  },
  {
    to: '/goods/purchase/register',
    label: 'Purchase register',
    permission: 'report:purchase_register:view',
  },
  { to: '/reports', label: 'Reports desk' },
  {
    to: '/reports/balance-sheet',
    label: '  Balance Sheet',
    permission: 'report:balance_sheet:view',
  },
  {
    to: '/reports/trial-balance',
    label: '  Trial Balance',
    permission: 'report:trial_balance:view',
  },
  {
    to: '/reports/day-book',
    label: '  Day Book',
    permission: 'report:day_book:view',
  },
  {
    to: '/reports/ledger',
    label: '  Ledger',
    permission: 'report:ledger:view',
  },
  {
    to: '/reports/group-summary',
    label: '  Group Summary',
    permission: 'report:group_summary:view',
  },
  {
    to: '/company/features',
    label: 'Features (F11)',
    permission: 'company:company:view',
  },
  {
    to: '/company/branches',
    label: 'Branches',
    permission: 'company:branch:view',
  },
  {
    to: '/company/financial-years',
    label: 'Financial years',
    permission: 'company:financial_year:view',
  },
  {
    to: '/company/period-locks',
    label: 'Period locks',
    permission: 'company:period_lock:view',
  },
  { to: '/users', label: 'Users', permission: 'admin:user:view' },
  { to: '/roles', label: 'Roles', permission: 'admin:role:view' },
  { to: '/audit', label: 'Edit log / activity', permission: 'audit:log:view' },
  {
    to: '/system/health',
    label: 'System health',
    permission: 'admin:system:view',
  },
];

export function AppShell() {
  const user = useSessionStore((s) => s.user);
  const tenant = useSessionStore((s) => s.tenant);
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const companies = useCompanyStore((s) => s.companies);
  const setFeatures = useCompanyStore((s) => s.setFeatures);
  const setCompanySwitcherOpen = useUiStore((s) => s.setCompanySwitcherOpen);
  const setCommandPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setCheatSheetOpen = useUiStore((s) => s.setCheatSheetOpen);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const pushToast = useUiStore((s) => s.pushToast);
  const can = usePermission();
  const navigate = useNavigate();
  const openGoodsVoucher = (direction: 'purchase' | 'sales') => {
    const event = new CustomEvent('goods-voucher:navigate', { cancelable: true, detail: { direction } });
    if (window.dispatchEvent(event)) navigate(`/goods/${direction}/new`);
  };

  const [financialYear, setFinancialYear] = useState<FinancialYear | null>(null);
  const [version, setVersion] = useState<string>('…');

  const activeCompany = companies.find((c) => c.id === activeCompanyId) ?? null;

  useEffect(() => {
    if (!activeCompanyId) return;
    companiesApi
      .getFeatures(activeCompanyId)
      .then(setFeatures)
      .catch(() => setFeatures(null));
    financialYearsApi
      .list()
      .then(({ items }) => setFinancialYear(items.find((f) => !f.isClosed) ?? items[0] ?? null))
      .catch(() => setFinancialYear(null));
  }, [activeCompanyId, setFeatures]);

  useEffect(() => {
    healthApi
      .liveness()
      .then((h) => setVersion(`${h.version} · ${h.build}`))
      .catch(() => setVersion('unknown'));
  }, []);

  useShortcuts('Global', [
    {
      id: 'new-sales',
      bindings: [{ key: 'F8' }],
      description: 'New Sales invoice',
      handler: () => openGoodsVoucher('sales'),
      allowInInputs: true,
      when: () => can('voucher:sales:create'),
    },
    {
      id: 'new-purchase',
      bindings: [{ key: 'F9' }],
      description: 'New Purchase voucher',
      handler: () => openGoodsVoucher('purchase'),
      allowInInputs: true,
      when: () => can('voucher:purchase:create'),
    },
    {
      id: 'new-item',
      bindings: [{ key: 'i', alt: true }],
      description: 'New item',
      handler: () => navigate('/masters/items'),
      allowInInputs: true,
      when: () => can('master:item:create'),
    },
    {
      id: 'new-contra',
      bindings: [{ key: 'F4' }],
      description: 'New Contra voucher',
      handler: () => navigate('/vouchers/new?type=contra'),
      allowInInputs: true,
      when: () => can('voucher:contra:create'),
    },
    {
      id: 'new-payment',
      bindings: [{ key: 'F5' }],
      description: 'New Payment voucher',
      handler: () => navigate('/money/payment/new'),
      allowInInputs: true,
      when: () => can('voucher:payment:create'),
    },
    {
      id: 'new-receipt',
      bindings: [{ key: 'F6' }],
      description: 'New Receipt voucher',
      handler: () => navigate('/money/receipt/new'),
      allowInInputs: true,
      when: () => can('voucher:receipt:create'),
    },
    {
      id: 'new-journal',
      bindings: [{ key: 'F7' }],
      description: 'New Journal voucher',
      handler: () => navigate('/vouchers/new?type=journal'),
      allowInInputs: true,
      when: () => can('voucher:journal:create'),
    },
    {
      id: 'company-switcher',
      bindings: [{ key: 'F3', alt: true }],
      description: 'Switch company',
      handler: () => setCompanySwitcherOpen(true),
      allowInInputs: true,
    },
    {
      id: 'command-palette',
      bindings: [{ key: 'k', ctrl: true }],
      description: 'Open command palette',
      handler: () => setCommandPaletteOpen(true),
      allowInInputs: true,
    },
    {
      id: 'cheat-sheet',
      bindings: [{ key: 'h', alt: true }],
      description: 'Show keyboard shortcuts for this screen',
      handler: () => setCheatSheetOpen(true),
      allowInInputs: true,
    },
    {
      id: 'context-help',
      bindings: [{ key: 'F1' }],
      description: 'Context help',
      handler: () =>
        pushToast({
          kind: 'info',
          message: 'Context help for this screen is not written yet — try Alt+H for shortcuts.',
        }),
      allowInInputs: true,
    },
    {
      id: 'toggle-theme',
      bindings: [{ key: 't', alt: true }],
      description: 'Toggle light / dark theme',
      handler: () => toggleTheme(),
      allowInInputs: true,
    },
  ]);

  return (
    <div className="flex h-screen flex-col bg-[var(--bg)] text-[var(--text)]">
      <header className="flex h-9 shrink-0 items-center justify-between border-b border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-[12px]">
        <div className="flex items-center gap-3 overflow-hidden">
          <span className="shrink-0 font-semibold tracking-tight">Khata</span>
          <span className="text-[var(--text-faint)]">|</span>
          <button
            onClick={() => setCompanySwitcherOpen(true)}
            className="flex items-center gap-1.5 truncate rounded-[3px] px-1.5 py-0.5 hover:bg-[var(--bg-hover)]"
            title="Switch company (Alt+F3)"
          >
            <span className="truncate font-medium">{activeCompany?.name ?? tenant?.name ?? '—'}</span>
            <span className="kbd">Alt+F3</span>
          </button>
          {financialYear && (
            <>
              <span className="text-[var(--text-faint)]">|</span>
              <span className="shrink-0 text-[var(--text-dim)]">
                FY {financialYear.startsOn.slice(0, 10)} → {financialYear.endsOn.slice(0, 10)}
                {financialYear.isClosed ? ' (closed)' : ''}
              </span>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            onClick={toggleTheme}
            className="rounded-[3px] px-1.5 py-0.5 text-[var(--text-dim)] hover:bg-[var(--bg-hover)]"
            title="Toggle theme (Alt+T)"
          >
            {theme === 'dark' ? 'Dark' : 'Light'}
          </button>
          <span className="text-[var(--text-dim)]">{user?.fullName}</span>
          <span className="font-mono text-[10.5px] text-[var(--text-faint)]">{version}</span>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <nav className="flex w-48 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-[var(--border)] bg-[var(--bg-elevated)] p-2">
          {NAV_ITEMS.map((item) => {
            if (item.to === '/vouchers' && !can('voucher:journal:view') && !can('voucher:contra:view')) return null;
            if (
              item.to === '/reports' &&
              ![
                'report:balance_sheet:view',
                'report:trial_balance:view',
                'report:day_book:view',
                'report:ledger:view',
                'report:group_summary:view',
              ].some(can)
            )
              return null;
            const link = (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  clsx(
                    'rounded-[3px] px-2 py-1 text-[12.5px]',
                    isActive
                      ? 'bg-[var(--bg-selected)] font-medium'
                      : 'text-[var(--text-dim)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]'
                  )
                }
              >
                {item.label}
              </NavLink>
            );
            return item.permission ? (
              <Can key={item.to} permission={item.permission}>
                {link}
              </Can>
            ) : (
              link
            );
          })}
          <OnboardingProgress />
          {can('company:company:create') && (
            <NavLink
              to="/companies/new"
              className={({ isActive }) =>
                clsx(
                  'rounded-[3px] px-2 py-1 text-[12.5px]',
                  isActive
                    ? 'bg-[var(--bg-selected)] font-medium'
                    : 'text-[var(--text-dim)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]'
                )
              }
            >
              New company
            </NavLink>
          )}
        </nav>

        <main className="min-w-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      <CompanySwitcher />
      <CommandPalette />
      <CheatSheetOverlay />
    </div>
  );
}
